/** @typedef {{ x: number; y: number }} Point */

/**
 * Map pointer to canvas bitmap coordinates (natural pixels).
 * @param {number} clientX
 * @param {number} clientY
 * @param {HTMLCanvasElement} canvas
 */
export function clientToCanvas(clientX, clientY, canvas) {
  const r = canvas.getBoundingClientRect();
  const sx = canvas.width / r.width;
  const sy = canvas.height / r.height;
  return {
    x: (clientX - r.left) * sx,
    y: (clientY - r.top) * sy,
  };
}

/**
 * Normalize rectangle so x,y is top-left and w,h positive.
 */
export function normalizeRect(x0, y0, x1, y1) {
  const x = Math.min(x0, x1);
  const y = Math.min(y0, y1);
  const w = Math.abs(x1 - x0);
  const h = Math.abs(y1 - y0);
  return { x, y, w, h };
}

/**
 * Snap arrow end to 8 directions from start, preserving length.
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 */
export function snapArrowEnd(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len < 1) return { x: x2, y: y2 };
  let ang = Math.atan2(dy, dx);
  const step = Math.PI / 4;
  ang = Math.round(ang / step) * step;
  return {
    x: x1 + Math.cos(ang) * len,
    y: y1 + Math.sin(ang) * len,
  };
}

/**
 * Squared distance from point to segment AB.
 */
export function distSqPointSegment(px, py, ax, ay, bx, by) {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq < 1e-8) return apx * apx + apy * apy;
  let t = (apx * abx + apy * aby) / abLenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + abx * t;
  const cy = ay + aby * t;
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy;
}

export function pointInRect(px, py, x, y, w, h) {
  return px >= x && px <= x + w && py >= y && py <= y + h;
}

/** @param {Point[]} points */
export function polylineMinDistSq(px, py, points) {
  if (!points || points.length < 2) return Infinity;
  let best = Infinity;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const d = distSqPointSegment(px, py, a.x, a.y, b.x, b.y);
    if (d < best) best = d;
  }
  return best;
}

const HANDLE_NAMES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/**
 * Corner handles only (nw, ne, se, sw) — design-tool style.
 * @returns {'nw'|'ne'|'se'|'sw'|null}
 */
export function hitCornerHandle(px, py, x, y, w, h, threshold) {
  const dim = Math.min(Math.max(w, 0), Math.max(h, 0));
  const cap = Math.max(5, dim * 0.22);
  const eff = Math.min(threshold, cap);
  const corners = [
    { name: 'nw', cx: x, cy: y },
    { name: 'ne', cx: x + w, cy: y },
    { name: 'se', cx: x + w, cy: y + h },
    { name: 'sw', cx: x, cy: y + h },
  ];
  const th2 = eff * eff;
  for (const c of corners) {
    const dx = px - c.cx;
    const dy = py - c.cy;
    if (dx * dx + dy * dy <= th2) return c.name;
  }
  return null;
}

/**
 * Hit test resize handle for axis-aligned rect (natural coords).
 * @returns {string | null}
 */
export function hitHandle(px, py, x, y, w, h, threshold) {
  const cx = [
    x,
    x + w / 2,
    x + w,
    x + w,
    x + w,
    x + w / 2,
    x,
    x,
  ];
  const cy = [
    y,
    y,
    y,
    y + h / 2,
    y + h,
    y + h,
    y + h,
    y + h / 2,
  ];
  const th2 = threshold * threshold;
  for (let i = 0; i < 8; i++) {
    const dx = px - cx[i];
    const dy = py - cy[i];
    if (dx * dx + dy * dy <= th2) return HANDLE_NAMES[i];
  }
  return null;
}

/**
 * Apply resize from handle drag (start rect + current pointer vs start pointer).
 */
export function applyRectResize(handle, sx, sy, sw, sh, dx, dy) {
  let { x, y, w, h } = { x: sx, y: sy, w: sw, h: sh };
  switch (handle) {
    case 'nw':
      x += dx;
      y += dy;
      w -= dx;
      h -= dy;
      break;
    case 'n':
      y += dy;
      h -= dy;
      break;
    case 'ne':
      y += dy;
      w += dx;
      h -= dy;
      break;
    case 'e':
      w += dx;
      break;
    case 'se':
      w += dx;
      h += dy;
      break;
    case 's':
      h += dy;
      break;
    case 'sw':
      x += dx;
      w -= dx;
      h += dy;
      break;
    case 'w':
      x += dx;
      w -= dx;
      break;
    default:
      break;
  }
  if (w < 4) {
    const shrink = 4 - w;
    if (handle === 'nw' || handle === 'w' || handle === 'sw') x -= shrink;
    w = 4;
  }
  if (h < 4) {
    const shrink = 4 - h;
    if (handle === 'nw' || handle === 'n' || handle === 'ne') y -= shrink;
    h = 4;
  }
  return { x, y, w, h };
}
